import { RepairRequest } from '../types/firestore';

export interface ServiceAssignmentResolution {
  assignedServiceUserId: string | null;
  canonicalServiceProviderId: string | null;
  legacyServiceId: string | null;
  hasConflict: boolean;
}

const normalizeIdentifier = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

/**
 * Source of truth for service assignment:
 * - canonical: `serviceProviderId`
 * - legacy visibility only: `serviceId` is exposed for diagnostics, not runtime assignment
 *
 * Conflict policy:
 * - if both fields exist and differ, canonical `serviceProviderId` wins.
 */
export const resolveServiceAssignment = (
  request?: Pick<RepairRequest, 'serviceProviderId' | 'serviceId'> | null
): ServiceAssignmentResolution => {
  const canonicalServiceProviderId = normalizeIdentifier(request?.serviceProviderId);
  const legacyServiceId = normalizeIdentifier(request?.serviceId);
  const hasConflict =
    Boolean(canonicalServiceProviderId) &&
    Boolean(legacyServiceId) &&
    canonicalServiceProviderId !== legacyServiceId;

  const assignedServiceUserId = canonicalServiceProviderId || null;

  return {
    assignedServiceUserId,
    canonicalServiceProviderId,
    legacyServiceId,
    hasConflict,
  };
};

export const getAssignedServiceUserId = (
  request?: Pick<RepairRequest, 'serviceProviderId' | 'serviceId'> | null
): string | null => resolveServiceAssignment(request).assignedServiceUserId;

export const hasConflictingServiceAssignment = (
  request?: Pick<RepairRequest, 'serviceProviderId' | 'serviceId'> | null
): boolean => resolveServiceAssignment(request).hasConflict;

export const isAssignedToServiceUser = (
  request: Pick<RepairRequest, 'serviceProviderId' | 'serviceId'> | null | undefined,
  userId?: string | null
): boolean => {
  const normalizedUserId = normalizeIdentifier(userId);
  if (!normalizedUserId) {
    return false;
  }
  return getAssignedServiceUserId(request) === normalizedUserId;
};
